import React, {useEffect, useState} from 'react';
import Button from '../../components/Button';

export type AiTeamReportData = {
  foreignTraits: { name: string; description: string }[];
  dominantTraits: { name: string; description: string }[];
  teamCultureAnalysis?: string;
};


type QuizEditDialogProps = {
  open: boolean;
  initialData: {
    reportUrl?: string;
    isReportApproved: boolean;
    aiReportData: AiTeamReportData | null;
  };
  onClose: () => void;
  onSave: (data: {
    isReportApproved: boolean;
    aiReportData: AiTeamReportData | null;
  }) => void;
  isLoading?: boolean;
};

export default function TeamApprovalModal({
                                            open,
                                            initialData,
                                            onClose,
                                            onSave,
                                            isLoading,
                                          }: QuizEditDialogProps) {
  const [isReportApproved, setIsReportApproved] = useState(
      initialData.isReportApproved,
  );
  const [foreignTraits, setForeignTraits] = useState(
      initialData.aiReportData?.foreignTraits || [],
  );
  const [dominantTraits, setDominantTraits] = useState(
      initialData.aiReportData?.dominantTraits || [],
  );
  const [teamCultureAnalysis, setTeamCultureAnalysis] = useState(
      initialData.aiReportData?.teamCultureAnalysis || '',
  );
  const handleSave = (reset: boolean = false) => {
    onSave({
      isReportApproved,
      aiReportData: reset
          ? null
          : {
            foreignTraits,
            dominantTraits,
            teamCultureAnalysis,
          },
    });
  };
  useEffect(() => {
    setIsReportApproved(initialData.isReportApproved);
    setForeignTraits(initialData.aiReportData?.foreignTraits || []);
    setDominantTraits(initialData.aiReportData?.dominantTraits || []);
    setTeamCultureAnalysis(initialData.aiReportData?.teamCultureAnalysis || '');
  }, [initialData]);

  const [hasTriggeredReload, setHasTriggeredReload] = useState(false);

  useEffect(() => {
    // Auto-reload if data is empty (no traits) or if first trait has empty name
    const shouldReload =
        !initialData.aiReportData?.foreignTraits?.length ||
        !initialData.aiReportData?.dominantTraits?.length ||
        (initialData.aiReportData?.foreignTraits?.length > 0 && initialData.aiReportData.foreignTraits[0].name === '');

    if (open && shouldReload && !hasTriggeredReload) {
      setHasTriggeredReload(true);
      handleSave(true);
    }
  }, [open, initialData.aiReportData, hasTriggeredReload, handleSave]);
  useEffect(() => {
    if (!open) {
      setHasTriggeredReload(false);
    }
  }, [open]);


  if (!open) return null;

  const handleForeignTraitChange = (index: number, value: string) => {
    const updated = [...foreignTraits];
    updated[index] = { ...updated[index], description: value };
    setForeignTraits(updated);
  };



  const handleDominantTraitChange = (index: number, value: string) => {
    const updated = [...dominantTraits];
    updated[index] = { ...updated[index], description: value };
    setDominantTraits(updated);
  };


  return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-bold mb-4">Edit AI Report Data</h2>

          {/* Dominant Traits */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Dominant Traits</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {dominantTraits.map((trait, index) => (
                  <div key={index}>
                    <label className="block text-sm font-medium mb-1">
                      {index+1}. {trait.name}
                    </label>
                    <textarea
                        value={trait.description}
                        onChange={(e) =>
                            handleDominantTraitChange(index, e.target.value)
                        }
                        rows={4}
                        className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
                    />
                  </div>
              ))}
            </div>
          </div>

          {/* Foreign Traits */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Foreign Traits</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {foreignTraits
                  .map((trait, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium mb-1">
                          {index + 10}. {trait.name}
                        </label>
                        <textarea
                            value={trait.description}
                            onChange={(e) =>
                                handleForeignTraitChange(index, e.target.value)
                            }
                            rows={4}
                            className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
                        />
                      </div>
                  ))}
            </div>
          </div>
          {/* Team Culture Analysis */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Team Culture Analysis</h3>
            <textarea
                value={teamCultureAnalysis}
                onChange={(e) => setTeamCultureAnalysis(e.target.value)}
                rows={6}
                className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
                placeholder="Enter overall team culture analysis..."
            />
          </div>

          {/* Approval */}
          <div className="mb-4 flex items-center gap-2">
            <input
                type="checkbox"
                checked={isReportApproved}
                onChange={(e) => setIsReportApproved(e.target.checked)}
                className="h-4 w-4"
            />
            <span className="text-sm">Approve</span>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mb-6">
            <Button onClick={onClose}>Close</Button>
            <Button disabled={isLoading} onClick={() => handleSave(true)}>
              Reload AI Data
            </Button>
            <Button
                disabled={isLoading}
                theme="primary"
                onClick={() => handleSave(false)}
            >
              Save
            </Button>
          </div>

          {/* PDF Preview */}
          {!isLoading && initialData.reportUrl && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-2">Report PDF Preview</h3>
                <a
                    href={initialData.reportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-2 text-blue-600 text-sm underline"
                >
                  Open in new tab
                </a>
                <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(
                        initialData.reportUrl,
                    )}&embedded=true`}
                    className="w-full h-[70vh] border border-gray-300 rounded"
                    title="Report PDF"
                />
              </div>
          )}
          {!initialData.reportUrl && (
              <div className="border-t pt-6">
                <p className="text-sm text-gray-500">
                  No report URL available. Please reload AI data to generate a new
                  report.
                </p>
              </div>
          )}
          {isLoading && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">Loading...</p>
              </div>
          )}
        </div>
      </div>
  );
}